import * as React from "react";
import Modal from "../../ui/modal/Modal";
import InvoicePaymentReleaseData from "../../../model/invoice/InvoicePaymentReleaseData";
import {Option} from "ts-option";
import {api} from "../../../api/api";
import {getErrorMessage, notifyBugsnagAndShowToast} from "../../../utils/Utils";
import Loading from "../../ui/loading/Loading";
import ButtonSecondary from "../../../hiberUiKit/src/atoms/Buttons/ButtonSecondary";
import ButtonPrimary from "../../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import CurrencyHelper from "../../../helper/CurrencyHelper";
import ButtonTertiary from "../../../hiberUiKit/src/atoms/Buttons/ButtonTertiary";
import IconWrapper from "../../../hiberUiKit/src/atoms/Icon/IconWrapper";
import {Icon} from "../../../hiberUiKit/src/atoms/Icon/Icon";
import classNames = require("classnames");
import _ = require("underscore");

interface ComponentProps {
	/**
	 * Invoice identifier.
	 */
	invoiceId: string;
	/**
	 * Invoice payment release data.
	 */
	invoicePaymentReleaseData: Option<InvoicePaymentReleaseData>;
	/**
	 * Close modal callback.
	 */
	onClose: () => void;
	/**
	 * Successful payment release callback.
	 */
	onSuccessfulPaymentRelease: () => void;
	/**
	 * Indicates if invoice is vat registered.
	 */
	vatRegistered: boolean;
}

interface State {
	/**
	 * Release partner payment confirmation visibility indicator.
	 */
	partnerPaymentConfirmationVisible: boolean
	/**
	 * Release hiber payment confirmation visibility indicator.
	 */
	hiberPaymentConfirmationVisible: boolean
	/**
	 * Indicating if request to server is in progress.
	 */
	isLoading: boolean;
}

type Props = ComponentProps;

export default class InvoicePaymentReleaseModal extends React.PureComponent<Props, State> {

	constructor(props: Props) {
		super(props);
		this.state = {
			partnerPaymentConfirmationVisible: false,
			hiberPaymentConfirmationVisible: false,
			isLoading: false
		};
	}

	render() {
		const renderConfirmation = this.state.hiberPaymentConfirmationVisible || this.state.partnerPaymentConfirmationVisible;
		return (
			<Modal customClass="c-invoice-details_release-payment-modal"
				   onCloseClicked={this.props.onClose}
				   title={"Payment release"} autoWidth={true}>
				<Loading isLoading={this.props.invoicePaymentReleaseData.isEmpty}>
					<div className="c-invoice-details_release-payment-modal_content-container">
						{!renderConfirmation && this.props.invoicePaymentReleaseData.map(this.renderPaymentsData).orNull}
						{renderConfirmation && this.renderConfirmation()}
					</div>
					<div className="c-invoice-details_release-payment-modal_horizontal-line"/>
					{this.renderButtons()}
				</Loading>
			</Modal>
		)
	}

	renderPaymentsData = (data: InvoicePaymentReleaseData) => {
		const vatInclusion = this.props.vatRegistered ? "excl" : "incl";
		return (
			<div className="c-invoice-details_release-payment-modal_payments-container">
				{this.renderPartnerData(data, vatInclusion)}
				{this.renderHiberData(data, vatInclusion)}
			</div>
		);
	};

	renderPartnerData = (data: InvoicePaymentReleaseData, vatInclusion: string) => {
		const partnerPaymContainerClassFromProps = classNames({
			"c-invoice-details_release-payment-modal_payment-details-container": true,
			"has-margin-right": true,
			"released": data.partnerPaymentReleased
		});

		return (
			<div className={partnerPaymContainerClassFromProps}>
				<div className="c-invoice-details_release-payment-modal_payment-details-container_internal-container">
					<div>
						<span className="c-invoice-details_release-payment-modal_payment-details-container_container-label">
							{`Partner payment (${vatInclusion}. VAT)`}
						</span>
						{this.renderLabelWithValue("Installer earnings", data.installerEarnings)}
						{data.selfSourcedItemsSubtotal != 0 &&
						this.renderLabelWithValue("Self-sourced parts", data.selfSourcedItemsSubtotal)}
						{data.partnerPartsMarginShare.map(share => this.renderLabelWithValue("Parts margin share", share)).orNull}
						{data.rewardsDiscount.map(discount =>
							this.renderLabelWithValue("Rewards discount value", Math.abs(discount))).orNull}
						{data.cardFee.map(fee => this.renderLabelWithValue("hiber processing fee", fee)).orNull}
						<div className="c-invoice-details_release-payment-modal_payment-details-container_horizontal-line"/>
						{data.partnerPaymentVat.map(vat => this.renderLabelWithValue("Sub-total (ex. VAT)",
							data.partnerPaymentSubtotal)).orNull}
						{data.partnerPaymentVat.map(vat => this.renderLabelWithValue("VAT", vat)).orNull}
						{data.partnerPaymentVat.map(vat =>
							<div className="c-invoice-details_release-payment-modal_payment-details-container_horizontal-line"/>
						).orNull}
						{this.renderLabelWithValue("Total", data.partnerPaymentTotal, true)}
					</div>
					<div className="c-invoice-details_release-payment-modal_payment-details-container_release-button">
						{data.partnerPaymentReleased ?
							<div className="c-invoice-details_release-payment-modal_payment-details-container_success">
								<IconWrapper icon={Icon.CheckMark} variant="green"/> Payment released
							</div>
							:
							<ButtonPrimary label={"Release"}
										   disabled={!data.partnerPaymentActive}
										   onClick={() => {this.setState({partnerPaymentConfirmationVisible: true})}}/>}
					</div>
				</div>
			</div>
		);
	};

	renderHiberData = (data: InvoicePaymentReleaseData, vatInclusion: string) => {
		const hiberPaymContainerClassFromProps = classNames({
			"c-invoice-details_release-payment-modal_payment-details-container": true,
			"released": data.hiberPaymentReleased
		});

		const hiberPaymContainerLabelClassFromProps = classNames({
			"c-invoice-details_release-payment-modal_payment-details-container_container-label": true,
			"disabled": !data.hiberPaymentActive
		});

		return (
			<div className={hiberPaymContainerClassFromProps}>
				<div className="c-invoice-details_release-payment-modal_payment-details-container_internal-container">
					<div>
						<span className={hiberPaymContainerLabelClassFromProps}>
							{`hiber Payment (${vatInclusion}. VAT)`}
						</span>
						{this.renderLabelWithValue("hiber Parts", data.hiberFee)}
						{this.renderLabelWithValue("Parts margin share", data.hiberPartsMarginShare)}
						{data.rewardsDiscount.map(discount =>
							this.renderLabelWithValue("Rewards discount value", discount)).orNull}
						{data.hiberLoanForPartner.map(loan => this.renderLabelWithValue("Loan for partner", loan)).orNull}
						{data.cardFee.map(fee => this.renderLabelWithValue("hiber processing fee", Math.abs(fee))).orNull}
						<div className="c-invoice-details_release-payment-modal_payment-details-container_horizontal-line"/>
						{data.hiberPaymentVat.map(
							vat => this.renderLabelWithValue("Sub-total (ex. VAT)", data.hiberPaymentSubtotal)).orNull}
						{data.hiberPaymentVat.map(vat => this.renderLabelWithValue("VAT", vat)).orNull}
						{data.hiberPaymentVat.map(vat =>
							<div className="c-invoice-details_release-payment-modal_payment-details-container_horizontal-line"/>
						).orNull}
						{this.renderLabelWithValue("Total", data.hiberPaymentTotal, true, !data.hiberPaymentActive)}
					</div>
					<div className="c-invoice-details_release-payment-modal_payment-details-container_release-button">
						{data.hiberPaymentReleased ?
							<div className="c-invoice-details_release-payment-modal_payment-details-container_success">
								<IconWrapper icon={Icon.CheckMark} variant="green"/> Payment released
							</div>
							:
							<ButtonPrimary label={"Release"}
										   disabled={!data.hiberPaymentActive}
										   onClick={() => {this.setState({hiberPaymentConfirmationVisible: true})}}/>}
					</div>
				</div>
			</div>
		);
	};

	renderConfirmation = () => {
		const paymentLabel = this.state.hiberPaymentConfirmationVisible ? "hiber" : "Partner";
		return (
			<div className="c-invoice-details_release-payment-modal_confirmation-container">
				<div className="c-invoice-details_release-payment-modal_confirmation-inside-container">
					<img src="/assets/img/icons/icn-warning-cmn.svg"/>
					<b>Are you sure?</b>
					<span>
						{paymentLabel} payment will be released immediately.
					</span>
				</div>
			</div>
		);
	};

	renderLabelWithValue(label: string, value: number, bold?: boolean, disabled?: boolean) {
		const classFromProps = classNames({
			"c-invoice-details_release-payment-modal_payment-details-container_label-with-value": true,
			"bold": bold,
			"disabled": disabled
		});
		return (
			<div className={classFromProps}>
				<span className="c-invoice-details_release-payment-modal_payment-details-container_label">{label}</span>
				<span>{CurrencyHelper.toCurrency(value, true)}</span>
			</div>
		);
	}

	renderButtons: () => JSX.Element = () => {
		let releaseButtonCallback: () => void;
		let backButtonCallback: () => void;
		if (this.state.partnerPaymentConfirmationVisible) {
			releaseButtonCallback = this.releasePartnerPayment;
			backButtonCallback = () => this.setState({partnerPaymentConfirmationVisible: false});
		}
		else if (this.state.hiberPaymentConfirmationVisible) {
			releaseButtonCallback = this.releaseHiberPayment;
			backButtonCallback = () => this.setState({hiberPaymentConfirmationVisible: false});
		}
		else {
			return (
				<div className="c-invoice-details_release-payment-modal_buttons-container">
					<ButtonSecondary label={"Done"} onClick={this.props.onClose}/>
				</div>
			);
		}
		return (
			<div className="c-invoice-details_release-payment-modal_buttons-container confirmation-buttons">
				<ButtonTertiary label={"← Back"} onClick={backButtonCallback}/>
				<ButtonPrimary label={"Release"}
							   disabled={this.state.isLoading}
							   isLoading={this.state.isLoading}
							   onClick={_.throttle(releaseButtonCallback, 300)}/>
			</div>
		);
	};

	releasePartnerPayment = () => this.setState({isLoading: true},
		() => api.instance.invoiceApi.releasePartnerPayment(this.props.invoiceId)
			.then(() => this.setState({partnerPaymentConfirmationVisible: false, isLoading: false},
				this.props.onSuccessfulPaymentRelease))
			.catch(error => {
				this.setState({isLoading: false});
				notifyBugsnagAndShowToast(error, "Release partner payment", getErrorMessage(error));
			})
	);

	releaseHiberPayment = () => this.setState({isLoading: true},
		() => api.instance.invoiceApi.releaseHiberPayment(this.props.invoiceId)
			.then(() => this.setState({hiberPaymentConfirmationVisible: false, isLoading: false},
				this.props.onSuccessfulPaymentRelease))
			.catch(error => {
				this.setState({isLoading: false});
				notifyBugsnagAndShowToast(error, "Release hiber payment", getErrorMessage(error));
			})
	);
}
